import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardList, Clock, User, UserPlus, Trash2, Play, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserRoles } from "@/hooks/useUserRoles";

interface OccupationalTest {
  id: string;
  name: string;
  description: string;
  test_type: string;
  time_limit_minutes: number;
  passing_score: number;
  is_active: boolean;
}

interface TestResult {
  id: string;
  candidate_id: string;
  test_id: string;
  score: number;
  percentage_score: number;
  status: string;
  completed_at: string;
  created_at: string;
  started_at?: string;
  candidate_name?: string;
  test_name?: string;
}

interface Candidate {
  id: string;
  full_name: string;
  status: string;
}

export function TestManagement() {
  const [tests, setTests] = useState<OccupationalTest[]>([]);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<string>("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isSuperAdmin, isAdmin, isUnassignedAdmin, adminTeamIds } = useUserRoles();

  useEffect(() => {
    fetchTests();
    fetchTestResults();
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      // Unassigned admins see no candidates
      if (isUnassignedAdmin) {
        setCandidates([]);
        return;
      }

      // Build query based on permissions
      let candidatesQuery = supabase
        .from('candidates')
        .select('id, full_name, status, created_by')
        .order('created_at', { ascending: false });

      // Team admins see candidates they created OR from managers in their teams
      if (isAdmin && !isSuperAdmin && adminTeamIds.length > 0) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Get all managers in the admin's teams
          const { data: teamManagers } = await supabase
            .from('manager_teams')
            .select('manager_id')
            .in('team_id', adminTeamIds);

          const managerIds = teamManagers?.map(m => m.manager_id) || [];
          const allowedCreatorIds = [user.id, ...managerIds];

          candidatesQuery = candidatesQuery.in('created_by', allowedCreatorIds);
        }
      }

      const { data, error } = await candidatesQuery;

      if (error) throw error;
      setCandidates(data || []);
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία φόρτωσης υποψηφίων",
        variant: "destructive",
      });
    }
  };

  const handleAssignTest = async (testId: string, candidateId: string) => {
    try {
      const { error } = await supabase
        .from('test_results')
        .insert({
          candidate_id: candidateId,
          test_id: testId,
          status: 'assigned',
          created_at: new Date().toISOString()
        } as any);

      if (error) throw error;

      toast({
        title: "Τεστ Ανατέθηκε",
        description: "Το τεστ ανατέθηκε επιτυχώς στον υποψήφιο",
      });

      fetchTestResults();
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία ανάθεσης τεστ",
        variant: "destructive",
      });
    }
  };

  const fetchTests = async () => {
    try {
      const { data, error } = await supabase
        .from('occupational_tests')
        .select('*')
        .eq('is_active', true)
        .neq('name', 'IPIP Personality Assessment') // Exclude the old IPIP test
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTests(data || []);
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία φόρτωσης τεστ",
        variant: "destructive",
      });
    }
  };

  const fetchTestResults = async () => {
    try {
      const { data, error } = await supabase
        .from('test_results')
        .select(`
          *,
          candidates (full_name),
          occupational_tests (name)
        `)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching test results:', error);
        setTestResults([]);
        return;
      }
      
      const formattedResults = data?.map(result => ({
        ...result,
        candidate_name: result.candidates?.full_name || 'Άγνωστος',
        test_name: result.occupational_tests?.name || 'Άγνωστο Τεστ'
      })) || [];

      setTestResults(formattedResults);
    } catch (error: any) {
      console.error('Exception fetching test results:', error);
      setTestResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async (testResultId: string) => {
    try {
      setDeleting(testResultId);
      
      const { error } = await supabase
        .from('test_results')
        .delete()
        .eq('id', testResultId);

      if (error) throw error;

      toast({
        title: "Επιτυχής Διαγραφή",
        description: "Το τεστ διαγράφηκε επιτυχώς",
      });

      // Refresh the list
      await fetchTestResults();
    } catch (error: any) {
      console.error('Error deleting test:', error);
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία διαγραφής τεστ",
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleContinueTest = (result: TestResult) => {
    // Navigate to continue the test based on test type and candidate
    if (result.test_id === 'ce3bb502-376e-4d76-a31a-7c1ddcf6a42f') {
      // IPIP-NEO test
      navigate(`/hr/candidate/${result.candidate_id}`);
    } else {
      // Other tests - navigate to test management or specific test page
      navigate(`/hr/candidate/${result.candidate_id}`);
    }
  };

  const handleViewDetails = (result: TestResult) => {
    // Navigate to detailed test results page
    navigate(`/hr/test-details/${result.id}`);
  };

  const handleDownloadReport = (result: TestResult) => {
    // Generate and download PDF report
    const reportData = {
      candidateName: result.candidate_name,
      testName: result.test_name,
      score: result.percentage_score,
      completedAt: result.completed_at,
      status: result.status
    };
    
    // Create a simple text report for now
    const reportContent = `
=== Αναφορά Τεστ ===
Υποψήφιος: ${reportData.candidateName}
Τεστ: ${reportData.testName}
Βαθμολογία: ${reportData.score}%
Ολοκληρώθηκε: ${new Date(reportData.completedAt).toLocaleString()}
Κατάσταση: ${reportData.status}

Δημιουργήθηκε στις: ${new Date().toLocaleString()}
    `;
    
    // Download as text file
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.candidate_name}_${result.test_name}_αναφορα.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Η Αναφορά Κατέβηκε",
      description: `Η αναφορά τεστ για τον ${result.candidate_name} κατέβηκε επιτυχώς`,
    });
  };

  const getTypeColor = (type: string) => {
    const typeLabels: Record<string, string> = {
      'personality': 'προσωπικότητα',
      'aptitude': 'ικανότητα',
      'technical': 'τεχνικό',
      'situational': 'καταστασιακό',
      'mmpi2': 'mmpi2'
    };
    
    switch (type) {
      case 'personality': return 'bg-blue-100 text-blue-800';
      case 'aptitude': return 'bg-green-100 text-green-800';
      case 'technical': return 'bg-purple-100 text-purple-800';
      case 'situational': return 'bg-orange-100 text-orange-800';
      case 'mmpi2': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type: string): string => {
    const typeLabels: Record<string, string> = {
      'personality': 'προσωπικότητα',
      'aptitude': 'ικανότητα',  
      'technical': 'τεχνικό',
      'situational': 'καταστασιακό',
      'mmpi2': 'mmpi2'
    };
    return typeLabels[type] || type;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'assigned': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string): string => {
    const statusLabels: Record<string, string> = {
      'completed': 'ολοκληρώθηκε',
      'in_progress': 'σε εξέλιξη',
      'assigned': 'ανατέθηκε',
      'expired': 'έληξε'
    };
    return statusLabels[status] || status;
  };

  if (loading) {
    console.log("TestManagement loading state, no Plus import should exist");
    return <div className="text-center py-8">Φόρτωση τεστ...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Available Tests */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Διαθέσιμα Τεστ</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tests.map((test) => (
            <Card key={test.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{test.name}</CardTitle>
                  <Badge className={getTypeColor(test.test_type)}>
                    {getTypeLabel(test.test_type)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  {test.description}
                </p>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {test.time_limit_minutes} λεπτά
                  </div>
                  <div>
                    Επιτυχία: {test.passing_score}%
                  </div>
                </div>
                <div className="flex space-x-2 mt-3">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="flex-1">
                        <UserPlus className="h-4 w-4 mr-1" />
                        Ανάθεση
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Ανάθεση Τεστ: {test.name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Select 
                          value={selectedCandidate} 
                          onValueChange={setSelectedCandidate}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Επιλέξτε υποψήφιο" />
                          </SelectTrigger>
                          <SelectContent>
                            {candidates.map((candidate) => (
                              <SelectItem key={candidate.id} value={candidate.id}>
                                {candidate.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button 
                          onClick={() => {
                            if (selectedCandidate) {
                              handleAssignTest(test.id, selectedCandidate);
                              setSelectedCandidate("");
                            }
                          }}
                          disabled={!selectedCandidate}
                          className="w-full"
                        >
                          Ανάθεση Τεστ
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent Test Results */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Πρόσφατα Αποτελέσματα Τεστ</h2>
        {testResults.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Δεν υπάρχουν αποτελέσματα τεστ.
          </div>
        ) : (
          <div className="grid gap-4">
            {testResults.map((result) => (
            <Card key={result.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {result.candidate_name}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">{result.test_name}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={getStatusColor(result.status)}>
                      {getStatusLabel(result.status)}
                    </Badge>
                    {result.percentage_score && (
                      <div className="text-lg font-bold mt-1">
                        {result.percentage_score}%
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div>
                    {result.completed_at && (
                      <p className="text-sm text-muted-foreground">
                        Ολοκληρώθηκε: {new Date(result.completed_at).toLocaleDateString('el-GR')}
                      </p>
                    )}
                    {result.status === 'in_progress' && (
                      <p className="text-sm text-muted-foreground">
                        Ξεκίνησε: {new Date(result.created_at).toLocaleDateString('el-GR')}
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    {result.status === 'in_progress' && (
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={() => handleContinueTest(result)}
                        className="flex items-center gap-1"
                      >
                        <Play className="h-4 w-4" />
                        Συνέχεια
                      </Button>
                    )}
                    {result.status === 'completed' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewDetails(result)}
                      >
                        <ClipboardList className="h-4 w-4 mr-1" />
                        Προβολή Λεπτομερειών
                      </Button>
                    )}
                    {result.status === 'completed' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDownloadReport(result)}
                      >
                        Κατέβασμα Αναφοράς
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={deleting === result.id}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Διαγραφή Τεστ</AlertDialogTitle>
                          <AlertDialogDescription>
                            Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το τεστ για τον {result.candidate_name}; 
                            Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteTest(result.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Διαγραφή
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        )}
      </div>

      {tests.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          Δεν υπάρχουν διαθέσιμα τεστ.
        </div>
      )}
    </div>
  );
}